import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { settingsApi } from '../api/payment';
import { VERSION_LABEL } from '../version';

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.026 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97H15.83c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}

export function LoginScreen() {
  const { signInWithGoogle, signInWithFacebook, signInWithEmail } = useAuthContext();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const supportEmail = useQuery({ queryKey: ['support-email'], queryFn: () => settingsApi.getAbout(), staleTime: 5 * 60_000 });
  const contact = supportEmail.data?.['about-contact-email'] ?? 'contato@homestock.app';
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingFacebook, setIsLoadingFacebook] = useState(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const handleGoogleLogin = async () => {
    setIsLoadingGoogle(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      addToast('Erro ao entrar com Google. Tente novamente.', 'error');
      setIsLoadingGoogle(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsLoadingFacebook(true);
    try {
      await signInWithFacebook();
    } catch (err) {
      addToast('Erro ao entrar com Facebook. Tente novamente.', 'error');
      setIsLoadingFacebook(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};
    if (!email) newErrors.email = 'Informe seu e-mail';
    if (!password) newErrors.password = 'Informe sua senha';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsLoadingEmail(true);
    try {
      await signInWithEmail(email, password);
      navigate('/');
    } catch (err: any) {
      addToast(err?.message ?? 'Usuário ou senha incorretos.', 'error');
    } finally {
      setIsLoadingEmail(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Emerald gradient top */}
      <div className="bg-gradient-to-b from-emerald-600 to-emerald-500 px-6 pt-16 pb-12 flex flex-col items-center text-white">
        <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mb-5 shadow-lg">
          <img src="/home-stock-transp-256x256.png" alt="HomeStock" className="w-11 h-11" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">HomeStock AI</h1>
        <p className="mt-2 text-emerald-100 text-center text-base">
          Controle de estoque doméstico inteligente
        </p>
      </div>

      {/* Form card */}
      <div className="flex-1 px-5 -mt-6">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-4">
          {/* Google */}
          <Button
            variant="secondary"
            fullWidth
            size="lg"
            onClick={handleGoogleLogin}
            loading={isLoadingGoogle}
            title="Entrar com Google"
            aria-label="Entrar com Google"
          >
            <GoogleIcon />
          </Button>

          {/* Facebook — adiado (decisão 2026-09-20): reativar quando o IdP do
              Facebook existir no Cognito (app na Meta + SSM params). */}
          {/* <Button
            variant="secondary"
            fullWidth
            size="lg"
            onClick={handleFacebookLogin}
            loading={isLoadingFacebook}
            title="Entrar com Facebook"
            aria-label="Entrar com Facebook"
          >
            <FacebookIcon />
          </Button> */}

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400 font-medium">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Email form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <Input
              label="E-mail"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              autoComplete="email"
              name="email"
            />
            <Input
              label="Senha"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              autoComplete="current-password"
              name="password"
            />
            <Button type="submit" variant="primary" fullWidth size="lg" loading={isLoadingEmail}>
              Entrar
            </Button>
          </form>

          {/* Help link */}
          <p className="text-center text-sm text-gray-500">
            Primeiro acesso?{' '}
            <a
              href={`mailto:${contact}`}
              className="text-emerald-600 font-medium hover:underline"
            >
              Fale conosco
            </a>
          </p>
        </div>
      </div>

      <div className="p-6 text-center space-y-0.5">
        <p className="text-xs text-gray-400">HomeStock AI &copy; {new Date().getFullYear()}</p>
        <p className="text-xs text-gray-300">{VERSION_LABEL}</p>
      </div>
    </div>
  );
}
